import { getConfig } from './config';
import { environment } from '../../environments/environment';
import { clearStoredAuthTokens } from '../utils/auth';
import { getHeadersWithToken } from './apiUitility';

const config = getConfig();

class RBACApiService {
  constructor() {
    this.baseURL = config.BASE_URL;
    this.organisationId = config.ORGANISATION_ID;
    this.userId = config.USER_ID;
    this.apiUrl = environment.controlplaneUrl;
  }

  // Resolve organisationId at call time to avoid undefined when instance
  // is constructed before login sets localStorage
  getOrgId() {
    return (
      this.organisationId ||
      (typeof window !== 'undefined' ? localStorage.getItem('org_id') : null) ||
      'bctglobal'
    );
  }

  // Helper method for API calls
  async apiCall(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = getHeadersWithToken();
    const config = {
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const errorData = await response.clone().json();
            if (errorData && errorData.error === "jwt expired") {
              clearStoredAuthTokens();
              window.location.href = '/#/login?sessionExpired=true';
            }
          } catch (e) {
            // Not JSON or other error
          }
        }
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ API Call Error:', error);
      throw error;
    }
  }

  // Helper method for API calls to environment URL
  async apiCallWithEnvironment(endpoint, options = {}) {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = getHeadersWithToken();
    const config = {
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        if (response.status === 401) {
          try {
            const errorData = await response.clone().json();
            if (errorData && errorData.error === "jwt expired") {
              clearStoredAuthTokens();
              window.location.href = '/#/login?sessionExpired=true';
            }
          } catch (e) {
            // Not JSON or other error
          }
        }
        throw new Error(`Environment API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Environment API Call Error:', error);
      throw error;
    }
  }

  // ==================== AUTHENTICATION ====================
  async login(credentials) {
    return this.apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async logout() {
    return this.apiCall('/auth/logout', {
      method: 'POST',
    });
  }

  async getCurrentUser(userEmail = null) {
    let response;
    if (userEmail) {
      // Use specific user email from localStorage
      const queryParams = new URLSearchParams({
        email: userEmail,
        organisationId: this.getOrgId(),
      });
      response = await this.apiCall(`/users/${userEmail}?${queryParams}`);
    } else {
      // Fallback to generic current user endpoint
      response = await this.apiCall('/auth/me');
    }

    // Map the role if response contains user data
    if (response && response.user) {
      response.user.role = this.mapUserRole(response.user.role);
    } else if (response && response.role) {
      response.role = this.mapUserRole(response.role);
    }

    return response;
  }

  async updateProfile(profileData) {
    return this.apiCall('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  // ==================== USER MANAGEMENT ====================
  async getUsers(params = {}) {
    const queryParams = new URLSearchParams({
      organisationId: this.getOrgId(),
      ...params,
    });

    try {
      const response = await this.apiCall(`/users?${queryParams}`);

      // Map the real backend response to RBAC format
      if (response && response.users) {
        // Object with users array
        const mappedUsers = response.users.map(user => {
          // Dynamic field mapping - handle different possible field names
          const userId = user._id || user.id || user.userId || user.user_id;
          const userName = user.userName || user.name || user.fullName || user.displayName || user.username;
          const userEmail = user.emailId || user.email || user.emailAddress || user.email_address;
          const userRole = user.role || user.userRole || user.user_role || user.permission;
          const userStatus = user.status || user.userStatus || user.user_status || user.active;
          const userStartDate = user.startDate || user.createdAt || user.created_at || user.joinDate;
          const userEndDate = user.endDate || user.terminatedAt || user.terminated_at;

          return {
            id: userId,
            name: userName || 'Unknown User',
            email: userEmail || 'no-email@example.com',
            role: this.mapUserRole(userRole),
            status: this.mapUserStatus(userStatus),
            organisationId: this.getOrgId(),
            startDate: userStartDate,
            endDate: userEndDate,
            userGroups: [], // Will be populated from groups endpoint
            projects: [], // Will be populated from projects endpoint
            specialPermissions: {
              canAddCrossGroupMembers: this.mapUserRole(userRole) === 'organisation_admin',
              canManageUserGroups: []
            }
          };
        });

        return mappedUsers;
      }

      return response;
    } catch (error) {
      console.error('Error fetching users:', error);
      return null;
    }
  }

  // Helper method to map backend roles to RBAC roles
  mapUserRole(backendRole) {
    if (!backendRole) return 'user';

    const role = backendRole.toString().toUpperCase();
    switch (role) {
      case 'ADMIN':
      case 'ORGANISATION_ADMIN':
      case 'ORG_ADMIN':
        return 'organisation_admin';
      case 'USER_GROUP_ADMIN':
      case 'GROUP_ADMIN':
      case 'TEAM_ADMIN':
        return 'user_group_admin';
      case 'USER':
      case 'MEMBER':
      case 'EMPLOYEE':
      case 'VIEWER':
        return 'user';
      default:
        return 'user';
    }
  }

  // Helper method to map backend status to RBAC status
  mapUserStatus(backendStatus) {
    if (!backendStatus) return 'inactive';

    const status = backendStatus.toString().toUpperCase();
    switch (status) {
      case 'ACTIVE':
      case 'ENABLED':
      case 'ACTIVE':
        return 'active';
      case 'INACTIVE':
      case 'DISABLED':
      case 'SUSPENDED':
        return 'inactive';
      default:
        return 'inactive';
    }
  }

  async createUser(userData) {
    return this.apiCall('/api/users', {
      method: 'POST',
      body: JSON.stringify({
        ...userData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async getUser(userId) {
    return this.apiCall(`/api/users/${userId}?organisationId=${this.getOrgId()}`);
  }

  async updateUser(userId, userData) {
    return this.apiCall(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...userData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async deleteUser(userId) {
    return this.apiCall(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async updateUserStatus(userId, status) {
    return this.apiCall(`/api/users/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({
        status,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async changeUserRole(userId, newRole) {
    return this.apiCall(`/api/users/${userId}/change-role`, {
      method: 'POST',
      body: JSON.stringify({
        newRole,
        organisationId: this.getOrgId(),
      }),
    });
  }

  // ==================== USER PERMISSIONS ====================

  // Get user-level permissions (organization-wide)
  async getUserLevelPermissions(userId) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/user-permissions?organisationId=${this.getOrgId()}`);

      console.log('🔐 User-level permissions response:', response);

      if (response && response.permissions) {
        return {
          userId: userId,
          organisationId: this.getOrgId(),
          permissions: {
            canManageUsers: response.permissions.canManageUsers || false,
            canInviteCrossGroupUsers: response.permissions.canInviteCrossGroupUsers || false,
            hasAccess: response.permissions.hasAccess !== undefined ? response.permissions.hasAccess : true,
            isOrgAdmin: response.permissions.isOrgAdmin || false,
            isGroupAdmin: response.permissions.isGroupAdmin || false,
            canAddCrossGroupUsers: response.permissions.canAddCrossGroupUsers || false,
            canManageProjectUsers: response.permissions.canManageProjectUsers || false,
            canEditProjectSettings: response.permissions.canEditProjectSettings || false
          },
          lastUpdated: new Date().toISOString()
        };
      }

      // Default restrictive permissions if no response
      return {
        userId: userId,
        organisationId: this.getOrgId(),
        permissions: {
          canManageUsers: false,
          canInviteCrossGroupUsers: false,
          hasAccess: true,
          isOrgAdmin: false,
          isGroupAdmin: false,
          canAddCrossGroupUsers: false,
          canManageProjectUsers: false,
          canEditProjectSettings: false
        },
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Error fetching user-level permissions:', error);
      // Return default restrictive permissions on error
      return {
        userId: userId,
        organisationId: this.getOrgId(),
        permissions: {
          canManageUsers: false,
          canInviteCrossGroupUsers: false,
          canAddCrossGroupUsers: false,
          canManageProjectUsers: false,
          canEditProjectSettings: false
        },
        lastUpdated: new Date().toISOString()
      };
    }
  }

  // Update user organization-level permissions (matches new backend API)
  async updateUserOrgPermissions(userId, permissions) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/org-permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organisationId: this.getOrgId(),
          permissions: {
            canManageUsers: permissions.canManageUsers || false,
            canInviteCrossGroupUsers: permissions.canInviteCrossGroupUsers || false,
            canAddCrossGroupUsers: permissions.canAddCrossGroupUsers || false,
            canManageProjectUsers: permissions.canManageProjectUsers || false,
            canEditProjectSettings: permissions.canEditProjectSettings || false
          },
          updatedBy: localStorage.getItem('user_email') || 'admin@example.com'
        }),
      });

      console.log('✅ User organization permissions updated:', response);
      return response;
    } catch (error) {
      console.error('❌ Error updating user organization permissions:', error);
      throw error;
    }
  }

  // Get user-project level permissions (project-specific)
  async getUserProjectPermissions(userId, projectId) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/project-permissions/${projectId}?organisationId=${this.getOrgId()}`);

      console.log('🔐 User-project permissions response:', response);

      if (response && response.permissions) {
        return {
          userId: userId,
          projectId: projectId,
          organisationId: this.getOrgId(),
          permissions: {
            canView: response.permissions.canView || false,
            canEdit: response.permissions.canEdit || false,
            canDelete: response.permissions.canDelete || false,
            canInviteCrossGroupUsers: response.permissions.canInviteCrossGroupUsers || false,
            canManageUsers: response.permissions.canManageUsers || false
          },
          lastUpdated: new Date().toISOString()
        };
      }

      // Default restrictive permissions if no response
      return {
        userId: userId,
        projectId: projectId,
        organisationId: this.getOrgId(),
        permissions: {
          canView: false,
          canEdit: false,
          canDelete: false,
          canInviteCrossGroupUsers: false,
          canManageUsers: false
        },
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Error fetching user-project permissions:', error);
      // Return default restrictive permissions on error
      return {
        userId: userId,
        projectId: projectId,
        organisationId: this.getOrgId(),
        permissions: {
          canView: false,
          canEdit: false,
          canDelete: false,
          canInviteCrossGroupUsers: false,
          canManageUsers: false
        },
        lastUpdated: new Date().toISOString()
      };
    }
  }

  async getUserPermissions(userId) {
    try {
      // Use the exact API structure you provided
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/permissions?organisationId=${this.getOrgId()}`);

      console.log('🔐 Raw permissions response:', response);

      if (response) {
        // NOTE: The current API response doesn't include permission flags yet.
        // We're inferring permissions from the data access patterns.
        // Once you implement the permission flags in the backend, this inference won't be needed.
        const permissions = response.permissions || {};
        const userGroups = permissions.userGroups || [];
        const projectAccess = permissions.projectAccess || [];

        // Check if user is admin of any group
        const isAdminOfAnyGroup = userGroups.some(group =>
          group.admins && group.admins.includes(userId)
        );

        // Check if user has any project access
        const hasProjectAccess = projectAccess.length > 0;

        // Infer permissions based on data access
        const inferredPermissions = {
          // Boolean permissions - infer from data access
          canViewUserGroups: userGroups.length > 0,
          canCreateUserGroups: isAdminOfAnyGroup || userGroups.length > 0, // If they have groups, they can likely create more
          canAddRemoveGroupMembers: isAdminOfAnyGroup,
          canAddCrossGroupUsersToProjects: isAdminOfAnyGroup,
          canAddUsersToAllProjectsInGroup: isAdminOfAnyGroup,

          // Array permissions - derive from actual data
          canCreateProjects: userGroups.map(group => group._id || group.id), // Can create in groups they have access to
          canAddRemoveProjectAdmins: hasProjectAccess ? projectAccess.map(p => p.projectId) : [],
          canAddCrossGroupUsersToGroupProjects: userGroups.map(group => group._id || group.id),
          canAddCrossGroupUsersToProject: hasProjectAccess ? projectAccess.map(p => p.projectId) : [],
          canEditProjectMemberAccess: hasProjectAccess ? projectAccess.map(p => p.projectId) : [],

          // Data arrays
          userGroups: userGroups,
          projectAccess: projectAccess,

          // Metadata
          userId: userId,
          organisationId: response.organisationId,
          lastUpdated: new Date().toISOString(),

          // Debug info
          _inferredFromData: true,
          _isAdminOfAnyGroup: isAdminOfAnyGroup,
          _hasProjectAccess: hasProjectAccess
        };

        console.log('🔐 Inferred permissions:', inferredPermissions);
        return inferredPermissions;
      }

      // Return default restrictive permissions if no response
      return {
        userId: userId,
        canViewUserGroups: false,
        canCreateUserGroups: false,
        canAddRemoveGroupMembers: false,
        canAddCrossGroupUsersToProjects: false,
        canAddUsersToAllProjectsInGroup: false,
        canCreateProjects: [],
        canAddRemoveProjectAdmins: [],
        canAddCrossGroupUsersToGroupProjects: [],
        canAddCrossGroupUsersToProject: [],
        canEditProjectMemberAccess: [],
        userGroups: [],
        projectAccess: [],
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('❌ Error fetching user permissions:', error);
      // Return default restrictive permissions on error
      return {
        userId: userId,
        canViewUserGroups: false,
        canCreateUserGroups: false,
        canAddRemoveGroupMembers: false,
        canAddCrossGroupUsersToProjects: false,
        canAddUsersToAllProjectsInGroup: false,
        canCreateProjects: [],
        canAddRemoveProjectAdmins: [],
        canAddCrossGroupUsersToGroupProjects: [],
        canAddCrossGroupUsersToProject: [],
        canEditProjectMemberAccess: [],
        userGroups: [],
        projectAccess: [],
        lastUpdated: new Date().toISOString()
      };
    }
  }

  // Update user-level permissions (organization-wide)
  async updateUserLevelPermissions(userId, permissions) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/user-permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          organisationId: this.getOrgId(),
          permissions: {
            canManageUsers: permissions.canManageUsers || false,
            canInviteCrossGroupUsers: permissions.canInviteCrossGroupUsers || false,
            hasAccess: permissions.hasAccess !== undefined ? permissions.hasAccess : true,
            isOrgAdmin: permissions.isOrgAdmin || false,
            isGroupAdmin: permissions.isGroupAdmin || false,
            canAddCrossGroupUsers: permissions.canAddCrossGroupUsers || false,
            canManageProjectUsers: permissions.canManageProjectUsers || false,
            canEditProjectSettings: permissions.canEditProjectSettings || false
          }
        }),
      });

      console.log('✅ User-level permissions updated:', response);
      return response;
    } catch (error) {
      console.error('❌ Error updating user-level permissions:', error);
      throw error;
    }
  }

  // Update user-project level permissions (project-specific)
  async updateUserProjectPermissions(userId, projectId, permissions) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/project-permissions/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          projectId,
          organisationId: this.getOrgId(),
          permissions: {
            canView: permissions.canView || false,
            canEdit: permissions.canEdit || false,
            canDelete: permissions.canDelete || false,
            canInviteCrossGroupUsers: permissions.canInviteCrossGroupUsers || false,
            canManageUsers: permissions.canManageUsers || false
          }
        }),
      });

      console.log('✅ User-project permissions updated:', response);
      return response;
    } catch (error) {
      console.error('❌ Error updating user-project permissions:', error);
      throw error;
    }
  }

  // Legacy method - now calls updateUserLevelPermissions for backward compatibility
  async updateUserPermissions(userId, permissions) {
    console.log('⚠️ updateUserPermissions is deprecated. Use updateUserLevelPermissions instead.');
    return this.updateUserLevelPermissions(userId, permissions);
  }

  async getUserGroups(userId) {
    try {
      const response = await this.apiCall(`/api/users/${userId}/groups?organisationId=${this.getOrgId()}`);

      // Map the response to a consistent format
      if (response && response.groups) {
        const mappedGroups = response.groups.map(group => {
          const groupId = group._id || group.id || group.groupId || group.group_id;
          const groupName = group.name || group.groupName || group.title || group.group_name;
          const userRole = group.userRole || group.role || group.memberRole || 'member';
          const joinedAt = group.joinedAt || group.joined_at || group.memberSince || group.createdAt;

          return {
            id: groupId,
            name: groupName || 'Unknown Group',
            userRole: userRole,
            joinedAt: joinedAt,
            isAdmin: userRole === 'admin' || userRole === 'group_admin',
            permissions: group.permissions || []
          };
        });

        return {
          userId: userId,
          groups: mappedGroups,
          totalGroups: mappedGroups.length,
          adminGroups: mappedGroups.filter(g => g.isAdmin).length
        };
      }

      return response;
    } catch (error) {
      console.error('❌ Error fetching user groups:', error);
      return null;
    }
  }

  async getUserProjects(userId) {
    try {
      const response = await this.apiCall(`/api/users/${userId}/projects?organisationId=${this.getOrgId()}`);

      // Map the response to a consistent format
      if (response && response.projects) {
        const mappedProjects = response.projects.map(project => {
          const projectId = project._id || project.id || project.projectId || project.project_id;
          const projectName = project.name || project.projectName || project.title || project.project_name;
          const userRole = project.userRole || project.role || project.accessLevel || 'viewer';
          const accessGranted = project.accessGranted || project.grantedAt || project.joinedAt || project.createdAt;

          return {
            id: projectId,
            name: projectName || 'Unknown Project',
            userRole: userRole,
            accessGranted: accessGranted,
            permissions: project.permissions || [],
            canEdit: userRole === 'editor' || userRole === 'admin' || userRole === 'owner',
            canView: true, // If user has access, they can view
            canDelete: userRole === 'admin' || userRole === 'owner'
          };
        });

        return {
          userId: userId,
          projects: mappedProjects,
          totalProjects: mappedProjects.length,
          editableProjects: mappedProjects.filter(p => p.canEdit).length
        };
      }

      return response;
    } catch (error) {
      console.error('Error fetching user projects:', error);
      return null;
    }
  }

  // Get comprehensive user data including permissions, groups, and projects
  async getUserCompleteData(userId) {
    try {
      const [userData, permissions, groups, projects] = await Promise.all([
        this.getUser(userId),
        this.getUserPermissions(userId),
        this.getUserGroups(userId),
        this.getUserProjects(userId)
      ]);

      return {
        user: userData,
        permissions: permissions,
        groups: groups,
        projects: projects,
        summary: {
          totalGroups: groups?.totalGroups || 0,
          adminGroups: groups?.adminGroups || 0,
          totalProjects: projects?.totalProjects || 0,
          editableProjects: projects?.editableProjects || 0,
          specialPermissions: permissions?.specialPermissions?.length || 0
        }
      };
    } catch (error) {
      console.error('Error fetching complete user data:', error);
      return null;
    }
  }

  // ==================== COMPREHENSIVE PERMISSION MANAGEMENT ====================

  // Assign permissions for first time
  async assignUserPermissions(userId, permissionData) {
    try {
      const response = await this.apiCall(`/api/users/${userId}/assign-permissions`, {
        method: 'POST',
        body: JSON.stringify({
          ...permissionData,
          organisationId: this.getOrgId(),
        }),
      });

      console.log('✅ Permissions assigned successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Error assigning permissions:', error);
      throw error;
    }
  }

  // Update user permissions
  async updateUserPermissions(userId, permissionData) {
    try {
      const response = await this.apiCall(`/api/users/${userId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({
          ...permissionData,
          organisationId: this.getOrgId(),
        }),
      });

      console.log('✅ Permissions updated successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Error updating permissions:', error);
      throw error;
    }
  }

  // Assign user to group
  async assignUserToGroup(userId, groupData) {
    try {
      const response = await this.apiCall(`/api/users/${userId}/assign-to-group`, {
        method: 'POST',
        body: JSON.stringify({
          ...groupData,
          organisationId: this.getOrgId(),
        }),
      });

      console.log('✅ User assigned to group successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Error assigning user to group:', error);
      throw error;
    }
  }

  // Assign user to project
  async assignUserToProject(userId, projectData) {
    try {
      const response = await this.apiCall(`/api/users/${userId}/assign-to-project`, {
        method: 'POST',
        body: JSON.stringify({
          ...projectData,
          organisationId: this.getOrgId(),
        }),
      });

      console.log('✅ User assigned to project successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Error assigning user to project:', error);
      throw error;
    }
  }

  // Remove user from group
  async removeUserFromGroup(userId, groupId) {
    try {
      const response = await this.apiCall(`/api/users/${userId}/remove-from-group/${groupId}`, {
        method: 'DELETE',
      });

      console.log('✅ User removed from group successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Error removing user from group:', error);
      throw error;
    }
  }

  // Remove user from project
  async removeUserFromProject(userId, projectId) {
    try {
      const response = await this.apiCall(`/api/users/${userId}/remove-from-project/${projectId}`, {
        method: 'DELETE',
      });

      console.log('✅ User removed from project successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Error removing user from project:', error);
      throw error;
    }
  }

  async grantSpecialPermission(userId, permissionData) {
    return this.apiCall(`/api/users/${userId}/special-permissions`, {
      method: 'POST',
      body: JSON.stringify({
        ...permissionData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async searchUsers(query, filters = {}) {
    const queryParams = new URLSearchParams({
      q: query,
      organisationId: this.getOrgId(),
      ...filters,
    });
    return this.apiCall(`/api/users/search?${queryParams}`);
  }

  async getUserStats() {
    return this.apiCall(`/api/users/stats?organisationId=${this.getOrgId()}`);
  }

  // ==================== USER GROUPS MANAGEMENT ====================
  async getGroups(params = {}) {
    const queryParams = new URLSearchParams({
      organisationId: this.getOrgId(),
      ...params,
    });

    try {
      const response = await this.apiCallWithEnvironment(`/manage/groups?${queryParams}`);

      // Map the real backend response to RBAC format
      if (response && response.userGroups) {
        const mappedGroups = response.userGroups.map(group => {
          // Dynamic field mapping - handle different possible field names
          const groupId = group._id || group.id || group.groupId || group.group_id;
          const groupName = group.name || group.groupName || group.title || group.group_name;
          const groupDescription = group.description || group.desc || group.groupDescription || group.group_description;
          const groupAdmins = group.admins || group.adminUsers || group.admin_users || group.administrators || [];
          const groupMembers = group.members || group.memberUsers || group.member_users || group.users || [];
          const groupStatus = group.active !== undefined ? (group.active ? 'active' : 'inactive') :
            (group.status || group.groupStatus || 'active');
          const groupCreatedBy = group.createdBy || group.created_by || group.owner || group.createdByUser;
          const groupCreatedAt = group.createdAt || group.created_at || group.createdDate;
          const groupUpdatedAt = group.updatedAt || group.updated_at || group.modifiedAt;

          // Handle projects array - extract IDs from project objects
          let groupProjects = group.projects || group.groupProjects || group.associatedProjects || [];
          if (Array.isArray(groupProjects) && groupProjects.length > 0) {
            // If projects are objects with _id, extract the IDs
            if (typeof groupProjects[0] === 'object' && groupProjects[0]._id) {
              groupProjects = groupProjects.map((project) => project._id || project.id);
            }
          }

          return {
            id: groupId,
            name: groupName || 'Unknown Group',
            description: groupDescription || 'No description available',
            admins: groupAdmins,
            members: groupMembers,
            projects: groupProjects,
            status: groupStatus,
            organisationId: this.getOrgId(),
            createdBy: groupCreatedBy || 'Unknown',
            createdAt: groupCreatedAt,
            updatedAt: groupUpdatedAt,
            memberCount: groupMembers.length,
            adminCount: groupAdmins.length
          };
        });

        return mappedGroups;
      }

      return response;
    } catch (error) {
      console.error('Error fetching groups:', error);
      return null;
    }
  }

  async createGroup(groupData) {
    return this.apiCallWithEnvironment('/manage/groups', {
      method: 'POST',
      body: JSON.stringify({
        ...groupData,
        organisationId: this.getOrgId(),
        createdBy: this.userId || (typeof window !== 'undefined' ? (localStorage.getItem('user_email') || localStorage.getItem('userId') || '') : ''),
      }),
    });
  }

  async getGroup(groupId) {
    return this.apiCallWithEnvironment(`/manage/groups/${groupId}?organisationId=${this.getOrgId()}`);
  }

  async updateGroup(groupId, groupData) {
    return this.apiCallWithEnvironment(`/manage/groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...groupData,
        organisationId: this.getOrgId(),
        updatedBy: localStorage.getItem('user_email') || 'admin@example.com',
      }),
    });
  }

  async deleteGroup(groupId) {
    return this.apiCallWithEnvironment(`/manage/groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  // Add project to group
  async addProjectToGroup(groupId, projectData) {
    return this.apiCallWithEnvironment(`/manage/groups/${groupId}/projects`, {
      method: 'POST',
      body: JSON.stringify({
        projectId: projectData.projectId,
        organisationId: this.getOrgId(),
        updatedBy: localStorage.getItem('user_email') || 'admin@example.com',
      }),
    });
  }

  // Remove project from group
  async removeProjectFromGroup(groupId, projectId) {
    return this.apiCallWithEnvironment(`/manage/groups/${groupId}/projects/${projectId}`, {
      method: 'DELETE',
      body: JSON.stringify({
        organisationId: this.getOrgId(),
        updatedBy: localStorage.getItem('user_email') || 'admin@example.com',
      }),
    });
  }

  // Get projects in a specific group
  async getGroupProjects(groupId) {
    return this.apiCallWithEnvironment(`/manage/groups/${groupId}/projects?organisationId=${this.getOrgId()}`);
  }

  // Get all groups with their projects
  async getAllGroupsWithProjects() {
    return this.apiCallWithEnvironment(`/manage/groups/projects/all?organisationId=${this.getOrgId()}`);
  }

  async getGroupMembers(groupId) {
    return this.apiCall(`/api/groups/${groupId}/members?organisationId=${this.getOrgId()}`);
  }

  async addGroupMember(groupId, memberData) {
    return this.apiCall(`/api/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({
        ...memberData,
        organisationId: this.getOrgId(),
        updatedBy: localStorage.getItem('user_email') || 'admin@example.com'
      }),
    });
  }

  async updateGroupMemberRole(groupId, userId, role) {
    return this.apiCall(`/groups/${groupId}/members/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({
        role,
        organisationId: this.getOrgId(),
        updatedBy: localStorage.getItem('user_email') || 'admin@example.com'
      }),
    });
  }

  async removeGroupMember(groupId, userId) {
    return this.apiCall(`/api/groups/${groupId}/members/${userId}?organisationId=${this.getOrgId()}`, {
      method: 'DELETE',
      body: JSON.stringify({
        updatedBy: localStorage.getItem('user_email') || 'admin@example.com'
      })
    });
  }

  async getGroupAdmins(groupId) {
    return this.apiCall(`/groups/${groupId}/admins?organisationId=${this.getOrgId()}`);
  }

  async addGroupAdmin(groupId, adminData) {
    return this.apiCall(`/api/groups/${groupId}/admins`, {
      method: 'POST',
      body: JSON.stringify({
        ...adminData,
        organisationId: this.getOrgId(),
        updatedBy: localStorage.getItem('user_email') || 'admin@example.com'
      }),
    });
  }

  async updateGroupAdminPermissions(groupId, userId, permissions) {
    return this.apiCall(`/api/groups/${groupId}/admins/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({
        permissions,
        organisationId: this.getOrgId(),
        updatedBy: localStorage.getItem('user_email') || 'admin@example.com'
      }),
    });
  }

  async removeGroupAdmin(groupId, userId) {
    return this.apiCall(`/api/groups/${groupId}/admins/${userId}?organisationId=${this.getOrgId()}`, {
      method: 'DELETE',
      body: JSON.stringify({
        updatedBy: localStorage.getItem('user_email') || 'admin@example.com'
      })
    });
  }

  async getGroupSettings(groupId) {
    return this.apiCall(`/api/groups/${groupId}/settings?organisationId=${this.getOrgId()}`);
  }

  async updateGroupSettings(groupId, settings) {
    return this.apiCall(`/api/groups/${groupId}/settings`, {
      method: 'PUT',
      body: JSON.stringify({
        ...settings,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async validateGroupAdmin(groupId, userId) {
    return this.apiCall(`/api/groups/${groupId}/validate-admin`, {
      method: 'POST',
      body: JSON.stringify({
        userId,
        organisationId: this.getOrgId(),
      }),
    });
  }

  // ==================== PROJECTS MANAGEMENT ====================
  async getProjects(params = {}) {
    // Build query parameters including pagination
    const queryParams = new URLSearchParams({
      organisationId: this.getOrgId(),
    });

    // Add pagination parameters if provided
    if (params.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    try {
      const response = await this.apiCall(`/projects?${queryParams}`);

      // Map the real backend response to RBAC format
      if (response && response.projects) {
        // Object with projects array and pagination
        const mappedProjects = response.projects.map(project => {
          // Dynamic field mapping - handle different possible field names
          const projectId = project._id || project.id || project.projectId || project.project_id;
          const projectName = project.projectName || project.name || project.title || project.project_name;
          const projectDescription = project.projectDescription || project.description || project.desc || project.project_description;
          const projectOwner = project.lastUpdatedByUser || project.owner || project.createdBy || project.created_by || project.userId;
          const projectStatus = project.active !== undefined ? (project.active ? 'active' : 'inactive') :
            (project.status || project.projectStatus || 'inactive');
          const projectType = project.projectType || project.type || project.category || project.project_type;
          const projectLanguage = project.language || project.lang || project.language_code || 'EN';
          const projectGeography = project.geography || project.geo || project.country || project.region || 'US';
          const projectLastUpdated = project.lastUpdatedDate || project.updatedAt || project.updated_at || project.modifiedAt;
          const projectConfig = project.configuration || project.config || project.settings || {};

          return {
            id: projectId,
            name: projectName || 'Unknown Project',
            description: projectDescription || 'No description available',
            owner: projectOwner || 'Unknown',
            status: projectStatus,
            projectType: projectType || 'Unknown',
            language: projectLanguage,
            geography: projectGeography,
            lastUpdated: projectLastUpdated,
            configuration: projectConfig,
            permissions: {
              canAddCrossGroupMembers: true, // Default permission
              editors: [],
              viewers: []
            }
          };
        });

        // Return with pagination metadata
        return {
          projects: mappedProjects,
          pagination: response.pagination || {
            page: params.page || 1,
            limit: params.limit || 10,
            total: mappedProjects.length,
            pages: Math.ceil(mappedProjects.length / (params.limit || 10))
          }
        };
      }

      return response;
    } catch (error) {
      console.error('Error fetching projects:', error);
      return null;
    }
  }

  async createProject(projectData) {
    return this.apiCall('/projects', {
      method: 'POST',
      body: JSON.stringify({
        ...projectData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async getProject(projectId) {
    return this.apiCall(`/api/projects/${projectId}?organisationId=${this.getOrgId()}`);
  }

  async updateProject(projectId, projectData) {
    return this.apiCall(`/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify({
        ...projectData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async deleteProject(projectId) {
    return this.apiCall(`/projects/${projectId}`, {
      method: 'DELETE',
    });
  }

  async getProjectPermissions(projectId) {
    return this.apiCall(`/api/projects/${projectId}/permissions?organisationId=${this.getOrgId()}`);
  }

  async updateProjectPermissions(projectId, permissions) {
    return this.apiCall(`/api/projects/${projectId}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({
        ...permissions,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async getProjectMembers(projectId) {
    return this.apiCall(`/api/projects/${projectId}/members?organisationId=${this.getOrgId()}`);
  }

  async addProjectMember(projectId, memberData) {
    return this.apiCall(`/api/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify({
        ...memberData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async updateProjectMemberRole(projectId, userId, role) {
    return this.apiCall(`/api/projects/${projectId}/members/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({
        role,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async removeProjectMember(projectId, userId) {
    return this.apiCall(`/api/projects/${projectId}/members/${userId}?organisationId=${this.getOrgId()}`, {
      method: 'DELETE',
    });
  }

  async getCrossGroupMembers(projectId) {
    return this.apiCall(`/api/projects/${projectId}/cross-group-members?organisationId=${this.getOrgId()}`);
  }

  async addCrossGroupMember(projectId, memberData) {
    return this.apiCall(`/api/projects/${projectId}/cross-group-members`, {
      method: 'POST',
      body: JSON.stringify({
        ...memberData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async removeCrossGroupMember(projectId, userId) {
    return this.apiCall(`/api/projects/${projectId}/cross-group-members/${userId}?organisationId=${this.getOrgId()}`, {
      method: 'DELETE',
    });
  }

  async updateCrossGroupAccess(projectId, enabled) {
    return this.apiCall(`/api/projects/${projectId}/cross-group-access`, {
      method: 'PUT',
      body: JSON.stringify({
        enabled,
        organisationId: this.getOrgId(),
      }),
    });
  }

  // ==================== AUDIT LOGS ====================
  async getAuditLogs(params = {}) {
    const queryParams = new URLSearchParams({
      organisationId: this.getOrgId(),
      ...params,
    });

    try {
      const response = await this.apiCall(`/rbac/activity-logs?${queryParams}`);

      // Map the real backend response to RBAC format
      if (response && response.activityLogs) {
        const mappedLogs = response.activityLogs.map(log => {
          // Dynamic field mapping - handle different possible field names
          const logId = log._id || log.id || log.logId || log.log_id;
          const logUserId = log.userId || log.user_id || log.user || log.userEmail || log.user_email;
          const logActionType = log.actionType || log.action_type || log.action || log.type;
          const logEntityType = log.entityType || log.entity_type || log.targetType || log.target_type;
          const logEntityId = log.entityId || log.entity_id || log.targetId || log.target_id;
          const logDescription = log.description || log.desc || log.message || log.summary;
          const logDetails = log.details || log.metadata || log.data || {};
          const logSuccess = log.success !== undefined ? log.success : true;
          const logTimestamp = log.timestamp || log.createdAt || log.created_at || log.date;

          // Map action type to RBAC format
          const mappedAction = this.mapActionType(logActionType);

          // Map severity based on success and action type
          const mappedSeverity = this.mapLogSeverity(logSuccess, logActionType);

          // Extract user name from userId (assuming it's an email)
          const userName = logUserId.includes('@') ? logUserId.split('@')[0] : logUserId;

          return {
            id: logId,
            action: mappedAction,
            details: logDescription,
            user: userName,
            userEmail: logUserId,
            target: logEntityType,
            type: logActionType?.toLowerCase().replace(/_/g, ' ') || 'unknown',
            severity: mappedSeverity,
            timestamp: logTimestamp,
            success: logSuccess,
            entityId: logEntityId,
            entityType: logEntityType,
            metadata: logDetails
          };
        });

        return mappedLogs;
      }

      return response;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return null;
    }
  }

  // Helper method to map action types to readable descriptions
  mapActionType(actionType) {
    if (!actionType) return 'Unknown Action';

    const action = actionType.toString().toUpperCase();
    switch (action) {
      case 'CREATE_USER_GROUP':
        return 'Created User Group';
      case 'UPDATE_USER_GROUP':
        return 'Updated User Group';
      case 'DELETE_USER_GROUP':
        return 'Deleted User Group';
      case 'ADD_GROUP_MEMBER':
        return 'Added Group Member';
      case 'REMOVE_GROUP_MEMBER':
        return 'Removed Group Member';
      case 'CREATE_USER':
        return 'Created User';
      case 'UPDATE_USER':
        return 'Updated User';
      case 'DELETE_USER':
        return 'Deleted User';
      case 'CREATE_PROJECT':
        return 'Created Project';
      case 'UPDATE_PROJECT':
        return 'Updated Project';
      case 'DELETE_PROJECT':
        return 'Deleted Project';
      case 'LOGIN':
        return 'User Login';
      case 'LOGOUT':
        return 'User Logout';
      case 'PERMISSION_CHANGE':
        return 'Changed Permissions';
      default:
        return actionType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    }
  }

  // Helper method to map severity based on success and action type
  mapLogSeverity(success, actionType) {
    if (!success) return 'error';

    const action = actionType?.toString().toUpperCase();
    if (['DELETE_USER_GROUP', 'DELETE_USER', 'DELETE_PROJECT', 'REMOVE_GROUP_MEMBER'].includes(action)) {
      return 'warning';
    }

    return 'info';
  }

  async getAuditLog(logId) {
    return this.apiCall(`/rbac/activity-logs/${logId}?organisationId=${this.getOrgId()}`);
  }

  async exportAuditLogs(params = {}) {
    const queryParams = new URLSearchParams({
      organisationId: this.getOrgId(),
      ...params,
    });
    return this.apiCall(`/rbac/activity-logs/export?${queryParams}`);
  }

  async getAuditStats() {
    return this.apiCall(`/rbac/activity-logs/stats?organisationId=${this.getOrgId()}`);
  }

  // ==================== RBAC CORE ====================
  async createUserGroup(groupData) {
    return this.apiCall('/rbac/user-groups', {
      method: 'POST',
      body: JSON.stringify({
        ...groupData,
        organisationId: this.getOrgId(),
        createdBy: this.userId || (typeof window !== 'undefined' ? (localStorage.getItem('user_email') || localStorage.getItem('userId') || '') : ''),
      }),
    });
  }

  async getUserGroups() {
    return this.apiCall(`/rbac/user-groups?organisationId=${this.getOrgId()}`);
  }

  async createProjectAccess(accessData) {
    return this.apiCall('/rbac/project-access', {
      method: 'POST',
      body: JSON.stringify({
        ...accessData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async getProjectAccess() {
    return this.apiCall(`/rbac/project-access?organisationId=${this.getOrgId()}`);
  }

  async createInvitation(invitationData) {
    return this.apiCall('/rbac/invitations', {
      method: 'POST',
      body: JSON.stringify({
        ...invitationData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async getInvitations(organisationIdOverride) {
    const orgId = organisationIdOverride || this.getOrgId() || (typeof window !== 'undefined' ? localStorage.getItem('org_id') : null) || 'bctglobal';
    const qp = new URLSearchParams({ organisationId: orgId });
    return this.apiCallWithEnvironment(`/analytics/invitations?${qp.toString()}`);
  }

  // ==================== INVITATION MANAGEMENT ====================
  async sendInvitation(invitationData) {
    // Ensure the invitation data includes organization-level permissions
    const enhancedInvitationData = {
      organisationId: this.getOrgId(),
      email: invitationData.email,
      invitedBy: localStorage.getItem('user_email') || 'admin@example.com',
      role: invitationData.role || 'user',
      permissions: invitationData.permissions || [],
      // Organization-level permissions structure matching backend
      orgPermissions: invitationData.orgPermissions || {
        canManageUsers: false,
        canInviteCrossGroupUsers: false,
        canAddCrossGroupUsers: false,
        canManageProjectUsers: false,
        canEditProjectSettings: false
      },
      message: invitationData.message || '',
      organisationName: invitationData.organisationName || this.getOrgId()
    };

    console.log('📧 Sending invitation with permissions:', enhancedInvitationData);

    return this.apiCallWithEnvironment('/analytics/invitations/send', {
      method: 'POST',
      body: JSON.stringify(enhancedInvitationData),
    });
  }

  async validateInvitationToken(token) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/invitations/validate/${token}`);
      console.log('🔍 Invitation token validated:', response);
      return response;
    } catch (error) {
      console.error('❌ Error validating invitation token:', error);
      throw error;
    }
  }

  async acceptInvitation(acceptanceData) {
    try {
      const response = await this.apiCallWithEnvironment('/analytics/invitations/accept', {
        method: 'POST',
        body: JSON.stringify(acceptanceData),
      });

      console.log('✅ Invitation accepted:', response);

      // If the invitation had orgPermissions, they should be automatically applied
      // by the backend when creating the user account
      if (response && response.user && response.user.orgPermissions) {
        console.log('🔐 User created with organization permissions:', response.user.orgPermissions);
      }

      return response;
    } catch (error) {
      console.error('❌ Error accepting invitation:', error);
      throw error;
    }
  }

  // Set user permissions after invitation acceptance
  async setUserPermissionsFromInvitation(userId, orgPermissions) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/org-permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organisationId: this.getOrgId(),
          permissions: orgPermissions,
          updatedBy: localStorage.getItem('user_email') || 'admin@example.com'
        }),
      });

      console.log('✅ User permissions set from invitation:', response);
      return response;
    } catch (error) {
      console.error('❌ Error setting user permissions from invitation:', error);
      throw error;
    }
  }

  async resendInvitation(invitationId, organisationName) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/invitations/${invitationId}/resend`, {
        method: 'POST',
        body: JSON.stringify({
          organisationName,
          organisationId: this.getOrgId()
        }),
      });

      console.log('📧 Invitation resent:', response);
      return response;
    } catch (error) {
      console.error('❌ Error resending invitation:', error);
      throw error;
    }
  }

  async cancelInvitation(invitationId, cancelledBy) {
    return this.apiCallWithEnvironment(`/analytics/invitations/${invitationId}?cancelledBy=${cancelledBy}`, {
      method: 'DELETE',
    });
  }

  // Get invitation details by ID (includes orgPermissions)
  async getInvitationById(invitationId) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/invitations/${invitationId}`);
      console.log('📧 Invitation details:', response);
      return response;
    } catch (error) {
      console.error('❌ Error fetching invitation details:', error);
      throw error;
    }
  }

  async getInvitationsByStatus(status = 'pending', organisationIdOverride) {
    const orgId = organisationIdOverride || this.getOrgId() || (typeof window !== 'undefined' ? localStorage.getItem('org_id') : null) || 'bctglobal';
    const qp = new URLSearchParams({ organisationId: orgId });
    return this.apiCallWithEnvironment(`/analytics/invitations?${qp.toString()}`);
  }

  // Get all invitations for the organization (includes orgPermissions)
  async getInvitations(organisationIdOverride) {
    const orgId = organisationIdOverride || this.getOrgId();
    const qp = new URLSearchParams({ organisationId: orgId });
    return this.apiCallWithEnvironment(`/analytics/invitations?${qp.toString()}`);
  }

  async checkPermission(permissionData) {
    return this.apiCallWithEnvironment('/analytics/rbac/check-permission', {
      method: 'POST',
      body: JSON.stringify({
        ...permissionData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  // ==================== BULK OPERATIONS ====================
  async bulkCreateUsers(users) {
    return this.apiCall('/api/users/bulk-create', {
      method: 'POST',
      body: JSON.stringify({
        users,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async bulkUpdateUsers(updates) {
    return this.apiCall('/api/users/bulk-update', {
      method: 'POST',
      body: JSON.stringify({
        updates,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async bulkDeleteUsers(userIds) {
    return this.apiCall('/api/users/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({
        userIds,
        organisationId: this.getOrgId(),
      }),
    });
  }

  // ==================== SEARCH & FILTERING ====================
  async searchUsers(query, filters = {}) {
    const queryParams = new URLSearchParams({
      q: query,
      organisationId: this.getOrgId(),
      ...filters,
    });
    return this.apiCall(`/api/users/search?${queryParams}`);
  }

  async searchGroups(query, filters = {}) {
    const queryParams = new URLSearchParams({
      q: query,
      organisationId: this.getOrgId(),
      ...filters,
    });
    return this.apiCall(`/api/groups/search?${queryParams}`);
  }

  async globalSearch(query) {
    return this.apiCall(`/api/search/global?q=${query}&organisationId=${this.getOrgId()}`);
  }

  // ==================== HEALTH CHECK ====================
  async healthCheck() {
    return this.apiCallWithEnvironment('/analytics/rbac/health');
  }

  // Test permissions endpoint
  async testPermissionsEndpoint(userId) {
    try {
      console.log('🧪 Testing permissions endpoint for user:', userId);
      console.log('🧪 Using organisationId:', this.getOrgId());
      console.log('🧪 Full URL:', `${environment.controlplaneUrl}/analytics/rbac/users/${userId}/permissions?organisationId=${this.getOrgId()}`);

      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/permissions?organisationId=${this.getOrgId()}`);
      console.log('🧪 Permissions endpoint response:', response);
      return response;
    } catch (error) {
      console.error('🧪 Permissions endpoint test failed:', error);
      throw error;
    }
  }

  // Create User Group
  async createUserGroup(groupData) {
    try {
      const response = await this.apiCallWithEnvironment('/analytics/rbac/user-groups', {
        method: 'POST',
        body: JSON.stringify({
          ...groupData,
          organisationId: this.getOrgId(),
          createdBy: this.userId || localStorage.getItem('user_email') || 'admin@yourorg.com'
        })
      });
      return response;
    } catch (error) {
      console.error('❌ Error creating user group:', error);
      throw error;
    }
  }

  // Assign Project Access
  async assignProjectAccess(assignmentData) {
    try {
      const response = await this.apiCallWithEnvironment('/analytics/rbac/assign-project-access', {
        method: 'POST',
        body: JSON.stringify({
          ...assignmentData,
          organisationId: this.getOrgId(),
          assignedBy: this.userId || localStorage.getItem('user_email') || 'admin@yourorg.com'
        })
      });
      return response;
    } catch (error) {
      console.error('❌ Error assigning project access:', error);
      throw error;
    }
  }

  // Update Project Permissions
  async updateProjectPermissions(updateData) {
    try {
      const response = await this.apiCallWithEnvironment('/analytics/rbac/update-project-permissions', {
        method: 'PUT',
        body: JSON.stringify({
          ...updateData,
          organisationId: this.getOrgId(),
          updatedBy: this.userId || localStorage.getItem('user_email') || 'admin@yourorg.com'
        })
      });
      return response;
    } catch (error) {
      console.error('❌ Error updating project permissions:', error);
      throw error;
    }
  }

  // Helper method to check if user can create projects in a specific group
  canCreateProjectInGroup(permissions, groupId) {
    return permissions.canCreateProjects && permissions.canCreateProjects.includes(groupId);
  }

  // Helper method to check if user can edit a specific project
  canEditProject(permissions, projectId) {
    const project = permissions.projectAccess?.find(p => p.projectId === projectId);
    return project && project.canEdit;
  }

  // Helper method to check if user can manage users in a project
  canManageProjectUsers(permissions, projectId) {
    return permissions.canEditProjectMemberAccess && permissions.canEditProjectMemberAccess.includes(projectId);
  }

  // ==================== ORGANISATION ====================
  async getOrganisation() {
    return this.apiCall(`/api/organisation?organisationId=${this.getOrgId()}`);
  }

  // Check if user is organization admin
  async isUserOrgAdmin(userId) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/org-admin?organisationId=${this.getOrgId()}`);
      return response?.isAdmin || false;
    } catch (error) {
      console.error('❌ Error checking if user is org admin:', error);
      return false;
    }
  }

  // Check if user is group admin for a specific group
  async isUserGroupAdmin(userId, groupId) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/group-admin/${groupId}`);
      return response?.isAdmin || false;
    } catch (error) {
      console.error('❌ Error checking if user is group admin:', error);
      return false;
    }
  }

  // Check if user is project admin for a specific project
  async isUserProjectAdmin(userId, projectId) {
    try {
      const response = await this.apiCallWithEnvironment(`/analytics/rbac/users/${userId}/project-admin/${projectId}?organisationId=${this.getOrgId()}`);
      return response?.isAdmin || false;
    } catch (error) {
      console.error('❌ Error checking if user is project admin:', error);
      return false;
    }
  }

  async updateOrganisation(orgData) {
    return this.apiCall('/api/organisation', {
      method: 'PUT',
      body: JSON.stringify({
        ...orgData,
        organisationId: this.getOrgId(),
      }),
    });
  }

  async getOrganisationStats() {
    return this.apiCall(`/api/organisation/stats?organisationId=${this.getOrgId()}`);
  }

  async getOrganisationSettings() {
    return this.apiCall(`/api/organisation/settings?organisationId=${this.getOrgId()}`);
  }

  async updateOrganisationSettings(settings) {
    return this.apiCall('/api/organisation/settings', {
      method: 'PUT',
      body: JSON.stringify({
        ...settings,
        organisationId: this.getOrgId(),
      }),
    });
  }

  // Get project permissions
  async getProjectPermissions(projectId, organisationId) {
    return this.apiCallWithEnvironment(`/analytics/rbac/project-permissions/${projectId}?organisationId=${organisationId}`);
  }

  // Get users in organization (RBAC endpoint)
  async getRbacUsers(organisationId) {
    return this.apiCallWithEnvironment(`/analytics/rbac/users?organisationId=${organisationId}`);
  }

  // Get user permissions for specific project
  async getUserProjectPermissions(userId, projectId, organisationId) {
    return this.apiCallWithEnvironment(`/analytics/rbac/my-project-permissions/${projectId}?userId=${userId}&organisationId=${organisationId}`);
  }

  // Get all user permissions
  async getAllUserPermissions(userId, organisationId) {
    return this.apiCallWithEnvironment(`/analytics/rbac/my-permissions?userId=${userId}&organisationId=${organisationId}`);
  }

  // Assign project permissions
  async assignProjectPermissions(assignmentData) {
    return this.apiCallWithEnvironment('/analytics/rbac/assign-project-permissions', {
      method: 'POST',
      body: JSON.stringify(assignmentData)
    });
  }

  // Update project permissions
  async updateProjectPermissions(updateData) {
    return this.apiCallWithEnvironment('/analytics/rbac/update-project-permissions', {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  // Remove project permissions
  async removeProjectPermissions(removeData) {
    return this.apiCallWithEnvironment('/analytics/rbac/remove-project-permissions', {
      method: 'DELETE',
      body: JSON.stringify(removeData)
    });
  }

  // ==================== GROUP → PROJECT BULK ASSIGN/UPDATE/REMOVE ====================
  async assignGroupToProject({ groupId, projectId, organisationId, role, assignedBy }) {
    return this.apiCallWithEnvironment('/analytics/rbac/assign-group-to-project', {
      method: 'POST',
      body: JSON.stringify({ groupId, projectId, organisationId, role, assignedBy })
    });
  }

  async updateGroupProjectRole({ groupId, projectId, organisationId, role, updatedBy }) {
    return this.apiCallWithEnvironment('/analytics/rbac/update-group-project-role', {
      method: 'PUT',
      body: JSON.stringify({ groupId, projectId, organisationId, role, updatedBy })
    });
  }

  async removeGroupProjectAccess({ groupId, projectId, organisationId, removedBy }) {
    return this.apiCallWithEnvironment('/analytics/rbac/remove-group-project-access', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groupId, projectId, organisationId, removedBy })
    });
  }

  // ==================== BATCH USER PERMISSIONS (NEW) ====================
  async bulkReadProjectPermissions({ projectId, organisationId, userIds }) {
    return this.apiCallWithEnvironment('/analytics/rbac/project-permissions/bulk-read', {
      method: 'POST',
      body: JSON.stringify({ projectId, organisationId, userIds })
    });
  }

  async batchUpdateProjectPermissions({ projectId, organisationId, updatedBy, assignments }) {
    return this.apiCallWithEnvironment('/analytics/rbac/batch-update-project-permissions', {
      method: 'POST',
      body: JSON.stringify({ projectId, organisationId, updatedBy, assignments })
    });
  }
}

// Create and export a singleton instance
const rbacApi = new RBACApiService();
export default rbacApi; 